import { Timestamp } from "@/types/date"

import { ensureArray } from "@/lib/utils/collections"
import { now } from "./date-utils"

// Types and function for a simple cache of objects. This would
// be more straightforward as a class, but since this is
// intended to be used in Redux slices -- which doesn't allow
// any types that can't be serialized as JSON -- we're using a
// functional approach.

export interface SimpleObjectCache<T> {
  entries: Array<SimpleCacheEntry<T>>
  maxEntries: number
}

export function createSimpleObjectCache<T>(
  maxEntries: number
): SimpleObjectCache<T> {
  return { entries: [], maxEntries }
}

export function add<T, K extends string | number>(
  cache: SimpleObjectCache<T>,
  objectOrObjects: T | T[],
  keyGetter: SimpleCacheKeyGetter<T, K>
) {
  const objects = ensureArray(objectOrObjects)
  const timestamp = now()
  const entries = cache.entries

  objects.forEach((object) => {
    const key = keyGetter(object)

    const existingIndex = entries.findIndex(
      (entry) => keyGetter(entry.object) === key
    )
    if (existingIndex === -1) {
      while (entries.length >= cache.maxEntries) {
        entries.shift()
      }
      entries.push({ object, timestamp })
    } else {
      const entry = entries[existingIndex]
      entry.timestamp = timestamp
      entry.object = object // replace the old instance
      entries.splice(existingIndex)
      entries.push(entry)
    }
  })
}

export function get<T, K extends number | string>(
  cache: SimpleObjectCache<T>,
  key: K,
  keyGetter: SimpleCacheKeyGetter<T, K>
): T | undefined {
  return cache.entries.find((entry) => keyGetter(entry.object) === key)?.object
}

export function allObjectsByKey<T, K extends number | string>(
  cache: SimpleObjectCache<T>,
  keyGetter: SimpleCacheKeyGetter<T, K>
): Map<K, T> {
  return new Map<K, T>(
    cache.entries.map((entry) => [keyGetter(entry.object), entry.object])
  )
}

interface SimpleCacheEntry<T> {
  object: T
  timestamp: Timestamp
}

type SimpleCacheKeyGetter<T, K extends string | number> = (object: T) => K
